import { useState } from "react";
import { ArrowRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { submitWaitlist } from "@/lib/waitlist";
import { trackEvent } from "@/lib/analytics";
import { cn } from "@/lib/cn";
import type { WaitlistVariant } from "@/components/WaitlistContext";

interface InlineEmailCaptureProps {
  variant?: WaitlistVariant;
  location: string;
  role?: string;
  heading?: string;
  description?: string;
  className?: string;
}

export function InlineEmailCapture({
  variant = "waitlist",
  location,
  role,
  heading,
  description,
  className,
}: InlineEmailCaptureProps) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus("submitting");
    try {
      await submitWaitlist({ email, message: role ? `Role: ${role}` : "", variant });
      trackEvent("Waitlist_Submit", { variant, source: location });
      setStatus("success");
    } catch {
      setStatus("error");
    }
  }

  if (status === "success") {
    return (
      <div className={cn("text-center", className)}>
        <p className="text-sm font-medium text-foreground">You're on the list! We'll be in touch.</p>
      </div>
    );
  }

  return (
    <div className={cn("w-full max-w-md mx-auto", className)}>
      {heading && <p className="mb-1 text-base font-semibold text-foreground">{heading}</p>}
      {description && <p className="mb-3 text-sm text-muted-foreground">{description}</p>}
      <form onSubmit={handleSubmit} className="flex gap-2">
        <label htmlFor={`email-${location}`} className="sr-only">Email address</label>
        <input
          id={`email-${location}`}
          type="email"
          required
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="h-11 flex-1 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        />
        <Button type="submit" disabled={status === "submitting"} className="shrink-0">
          {status === "submitting" ? "..." : <>Join <ArrowRight aria-hidden="true" className="ml-1 h-4 w-4" /></>}
        </Button>
      </form>
      {status === "error" && (
        <p className="mt-2 text-sm text-destructive">Something went wrong. Please try again.</p>
      )}
    </div>
  );
}
